import { ctaDesc, ctaTitle } from "@/lib/seo";
// /embassy/$country — per-country legalization landing
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import CountryDeepContent from "@/components/CountryDeepContent";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { EMBASSY_COUNTRIES, getEmbassy } from "@/data/embassies";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { EmbassyMarriageLinks } from "@/components/EmbassyMarriageLinks";
import { Building2, ShieldCheck, BadgeCheck, FileCheck2, Clock, Phone, MessageCircle, Languages } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/embassy/$country/")({
  loader: ({ params }) => {
    const c = getEmbassy(params.country);
    if (!c) throw notFound();
    return { slug: params.country };
  },
  head: ({ params }) => {
    const c = getEmbassy(params.country);
    if (!c) return { meta: [{ title: ctaTitle("ไม่พบประเทศ", "/embassy/$country", "th")}] };
    const url = `${SITE}/embassy/${c.slug}`;
    const title = `รับรองเอกสารสถานทูต${c.nameTh} ${c.flag} · เริ่ม ฿${c.serviceFeeTh.toLocaleString()} เสร็จ ${c.turnaround} | NYC`;
    const desc = `บริการรับรองเอกสารสถานทูต${c.nameTh} ครบทุกขั้นตอน: แปล → MFA → สถานทูต ${c.nameEn} ใน ${c.turnaround} — ${c.notes} — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/embassy/$country", "th") },
        { name: "description", content: ctaDesc(desc, "/embassy/$country", "th")},
        { name: "keywords", content: c.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/embassy/$country", "th")},
        { property: "og:description", content: ctaDesc(desc, "/embassy/$country", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"
        ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `รับรองเอกสารสถานทูต${c.nameTh}`,
              serviceType: "Consular Legalization & Apostille",
              areaServed: [
                { "@type": "Country", name: "Thailand" },
                { "@type": "Country", name: c.nameEn },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: c.serviceFeeTh,
                priceCurrency: "THB",
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          ...(c.faqs.length ? [{
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: c.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }] : []),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `วิธีรับรองเอกสารสถานทูต${c.nameTh}`,
              totalTime: "P7D",
              step: [
                { "@type": "HowToStep", position: 1, name: "ส่งเอกสาร", text: "ส่งสำเนาเอกสารผ่าน LINE @nycli — ทีมประเมินภายใน 15 นาที" },
                { "@type": "HowToStep", position: 2, name: "แปล + รับรอง", text: `แปลโดยนักแปลขึ้นทะเบียน (${c.acceptedLangs.join(" / ")}) พร้อมตราประทับ` },
                { "@type": "HowToStep", position: 3, name: "MFA Thailand", text: "นำเอกสารที่แปลแล้วยื่นกระทรวงการต่างประเทศ (Apostille หรือ Consular)" },
                { "@type": "HowToStep", position: 4, name: `สถานทูต${c.nameTh}`, text: `ยื่นสถานทูต/กงสุล${c.nameTh}ที่กรุงเทพฯ — รับเอกสารฉบับสมบูรณ์` },
                { "@type": "HowToStep", position: 5, name: "ส่งมอบ", text: `รับเอกสารฉบับจริงพร้อมไฟล์ PDF ภายใน ${c.turnaround}` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "รับรองสถานทูต", item: `${SITE}/embassy` },
                { "@type": "ListItem", position: 3, name: `รับรองสถานทูต${c.nameTh}`, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) }))
        ]),
      ],
    };
  },
  component: EmbassyCountryPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบประเทศ</h1>
      <Link to="/embassy" className="mt-4 inline-block text-gold underline">กลับไปยังรายการประเทศทั้งหมด</Link>
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
    </main>
  ),
});

function EmbassyCountryPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getEmbassy(slug)!;
  const others = EMBASSY_COUNTRIES.filter((x) => x.slug !== slug && x.region === c.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/embassy" className="hover:text-gold">รับรองสถานทูต</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Building2 className="h-3.5 w-3.5" /> Consular Legalization · {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{c.flag}</span>
            รับรองเอกสารสถานทูต{c.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{c.notes}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${c.serviceFeeTh.toLocaleString()}` },
              { k: "เสร็จภายใน", v: c.turnaround },
              { k: "ค่าสถานทูต", v: `฿${c.govFeeTh.toLocaleString()}` },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ส่งเอกสารผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061, ตั้งแต่ปี 2010) ให้บริการรับรองเอกสารสถานทูต{c.nameTh} ครบทุกขั้นตอน — แปลโดยนักแปลขึ้นทะเบียน + รับรอง MFA Thailand + ยื่นสถานทูต/กงสุล{c.nameTh} เริ่ม ฿{c.serviceFeeTh.toLocaleString()} เสร็จ {c.turnaround} บริการสำเร็จ 2,147+ เคส
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารที่นิยมยื่นสถานทูต{c.nameTh}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {c.topDocs.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ภาษาที่สถานทูตยอมรับ</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {c.acceptedLangs.map((l) => (
              <span key={l} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Languages className="h-3.5 w-3.5 text-amber-600" /> {l}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ขั้นตอนการรับรอง</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-5">
            {[
              { n: 1, t: "ส่งเอกสาร", d: "LINE / อีเมล" },
              { n: 2, t: "แปล + รับรอง", d: c.acceptedLangs[0] },
              { n: 3, t: "MFA Thailand", d: "Apostille / Consular" },
              { n: 4, t: `สถานทูต`, d: c.nameTh },
              { n: 5, t: "ส่งมอบ", d: c.turnaround },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคาเริ่มต้น</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            รับรองสถานทูต{c.nameTh} ฿{c.serviceFeeTh.toLocaleString()}/ฉบับ
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมแปล + รับรอง MFA + สถานทูตในราคาเดียว</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> มี Express 3 วันค่าเร่ง +฿3,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ส่ง EMS ฉบับจริงทั่วประเทศฟรี</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับงานเพิ่มได้: Apostille, Notary, Power of Attorney</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {c.faqs.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
              <FaqAccordion items={c.faqs} />
            </div>
          </section>
        )}

        {others.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศใกล้เคียง</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to="/embassy/$country"
                  params={{ country: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      {o.nameTh}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{o.serviceFeeTh.toLocaleString()} · {o.turnaround}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
        <EmbassyMarriageLinks country={c.slug} countryTh={c.nameTh} />
      </div>

      <CountryDeepContent lang="th" service="embassy" slug={c.slug} countryTh={c.nameTh} countryEn={c.nameEn} flag={c.flag} hague={false} priceFromTh={c.serviceFeeTh + c.govFeeTh} turnaround={c.turnaround} />
      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
    </main>
  );
}
